<template>
  <div class="org-usage-panel column card-cont-wrapper fade-in-out">
    <h3 class="nomargintopbottom">API & Storage Usage</h3>
    <div class="priorityBlock">
      <template v-if="headlineMode === 'under'">
        <div class="bigNumber feedback-success-text">
          €{{ formatFreeRemaining(freeRemaining) }}
        </div>
        <div
          class="smallText"
          :class="totalsLoading ? 'grayText' : 'feedback-success-text'"
        >
          <span v-if="totalsLoading">Processing…</span>
          <span v-else>free remaining</span>
        </div>
      </template>
      <template v-else-if="headlineMode === 'over'">
        <div class="bigNumber usage-danger-metric feedback-danger-text">
          €{{ formatSmallCurrency(overageEUR) }}
        </div>
        <div
          class="smallText usage-danger-metric"
          :class="totalsLoading ? 'grayText' : 'feedback-danger-text'"
        >
          <span v-if="totalsLoading">Processing…</span>
          <span v-else>to be debited</span>
        </div>
      </template>
      <template v-else>
        <div class="bigNumber grayText">Processing…</div>
      </template>
    </div>
    <div class="smallText grayText" style="margin-bottom: 6px">
      Totals 30d:
      <strong>
        <template v-if="totals30d">{{
          formatEUR(
            totals30d.totalCostEUR ??
              totals30d.totalCostUSD ??
              totals30d.approxCostEUR ??
              totals30d.approxCostUSD
          )
        }}</template>
        <template v-else>—</template>
      </strong>
      <span v-if="totals30d && hasStorage(totals30d)">
        (storage
        {{ formatEUR(totals30d.storageCostEUR ?? totals30d.storageCostUSD) }})
      </span>
      , All‑time:
      <strong>
        <template v-if="totalsAll">{{
          formatEUR(
            totalsAll.totalCostEUR ??
              totalsAll.totalCostUSD ??
              totalsAll.approxCostEUR ??
              totalsAll.approxCostUSD
          )
        }}</template>
        <template v-else>—</template>
      </strong>
      <span v-if="totalsAll && hasStorage(totalsAll)">
        (storage
        {{ formatEUR(totalsAll.storageCostEUR ?? totalsAll.storageCostUSD) }})
      </span>
    </div>

    <!-- NEW: Domain-Categorized Cost Breakdown -->
    <div
      v-if="
        totalsAll &&
        totalsAll.breakdown &&
        hasMeaningfulBreakdown(totalsAll.breakdown)
      "
      class="divMarginTop"
      style="
        background-color: var(--color-surface-highlight-faint);
        padding: var(--space-4);
        border-radius: var(--radius-sm);
      "
    >
      <h4
        class="smallText nomargintopbottom"
        style="margin-bottom: var(--space-1)"
      >
        Cost Breakdown (All-time)
      </h4>
      <div class="smallText">
        <div
          v-if="
            totalsAll.breakdown.transactions &&
            totalsAll.breakdown.transactions.costEUR > 0
          "
          class="row spaceBetween"
          style="margin-bottom: 2px"
        >
          <span>Transactions:</span>
          <strong>{{
            formatEUR(totalsAll.breakdown.transactions.costEUR)
          }}</strong>
        </div>
        <div
          v-if="
            totalsAll.breakdown.messages &&
            totalsAll.breakdown.messages.costEUR > 0
          "
          class="row spaceBetween"
          style="margin-bottom: 2px"
        >
          <span>Messages:</span>
          <strong>{{ formatEUR(totalsAll.breakdown.messages.costEUR) }}</strong>
        </div>
        <div
          v-if="
            totalsAll.breakdown.nfts && totalsAll.breakdown.nfts.costEUR > 0
          "
          class="row spaceBetween"
          style="margin-bottom: 2px"
        >
          <span>NFT Queries:</span>
          <strong>{{ formatEUR(totalsAll.breakdown.nfts.costEUR) }}</strong>
        </div>
        <div
          v-if="
            totalsAll.breakdown.products &&
            totalsAll.breakdown.products.costEUR > 0
          "
          class="row spaceBetween"
          style="margin-bottom: 2px"
        >
          <span>Products:</span>
          <strong>{{ formatEUR(totalsAll.breakdown.products.costEUR) }}</strong>
        </div>
        <div
          v-if="
            totalsAll.breakdown.services &&
            totalsAll.breakdown.services.costEUR > 0
          "
          class="row spaceBetween"
          style="margin-bottom: 2px"
        >
          <span>Services:</span>
          <strong>{{ formatEUR(totalsAll.breakdown.services.costEUR) }}</strong>
        </div>
        <div
          v-if="
            totalsAll.breakdown.items && totalsAll.breakdown.items.costEUR > 0
          "
          class="row spaceBetween"
          style="margin-bottom: 2px"
        >
          <span>Items:</span>
          <strong>{{ formatEUR(totalsAll.breakdown.items.costEUR) }}</strong>
        </div>
        <div
          v-if="
            totalsAll.breakdown.members &&
            totalsAll.breakdown.members.costEUR > 0
          "
          class="row spaceBetween"
          style="margin-bottom: 2px"
        >
          <span>Members:</span>
          <strong>{{ formatEUR(totalsAll.breakdown.members.costEUR) }}</strong>
        </div>
        <div
          v-if="
            totalsAll.breakdown.externalAPIs &&
            totalsAll.breakdown.externalAPIs.costEUR > 0
          "
          class="row spaceBetween"
          style="
            margin-bottom: 2px;
            border-top: 1px solid var(--color-border-default);
            padding-top: 4px;
            margin-top: 4px;
          "
        >
          <span>
            External APIs
            <span
              class="mono tiny"
              style="opacity: 0.7"
              title="Pass-through costs for Alchemy, Stripe, etc."
              >ⓘ</span
            >
          </span>
          <strong>{{
            formatEUR(totalsAll.breakdown.externalAPIs.costEUR)
          }}</strong>
        </div>
        <div
          v-if="
            totalsAll.breakdown.storage &&
            totalsAll.breakdown.storage.costEUR > 0
          "
          class="row spaceBetween"
          style="margin-bottom: 2px"
        >
          <span>Storage:</span>
          <strong>{{ formatEUR(totalsAll.breakdown.storage.costEUR) }}</strong>
        </div>
        <div
          v-if="
            totalsAll.breakdown.other && totalsAll.breakdown.other.costEUR > 0
          "
          class="row spaceBetween"
          style="margin-bottom: 2px"
        >
          <span>Other:</span>
          <strong>{{ formatEUR(totalsAll.breakdown.other.costEUR) }}</strong>
        </div>
      </div>
      <div
        class="smallText grayText"
        style="margin-top: var(--space-1); font-size: 0.8em"
      >
        Detailed breakdown by data domain. External API costs are pass-through
        charges for services like Alchemy.
      </div>
    </div>

    <!-- Daily Usage Cost Chart -->
    <div
      v-if="!tempDisableChart && usageSeries && usageSeries.length > 0"
      class="divMarginTop"
    >
      <h4
        class="smallText nomargintopbottom"
        style="margin-bottom: var(--space-4)"
      >
        Daily Usage Costs (30 days)
      </h4>
      <div style="height: 200px; position: relative">
        <canvas ref="usageChartCanvas"></canvas>
      </div>
    </div>

    <div
      v-if="error"
      class="smallText feedback-danger-text"
      style="margin-top: 4px"
    >
      <template v-if="error.code === 'CORS_DEV_BLOCKED'">
        Usage unavailable in development due to browser CORS. It will work after
        deploy.
      </template>
      <template v-else>
        {{ error.message || String(error) }}
      </template>
    </div>
    <!-- Breakdown by source (only if more than one) -->
    <div class="divMarginTop" v-if="(breakdown.bySource?.length || 0) > 1">
      <h4 class="smallText nomargintopbottom">Breakdown by source</h4>
      <div class="smallText grayText" v-if="breakdownLoading">Processing…</div>
      <div v-else>
        <ul class="smallText">
          <li v-for="src in breakdown.bySource" :key="src.source">
            <strong>{{ src.source }}</strong> —
            {{ formatEUR(src.approxCostEUR ?? src.approxCostUSD) }}
          </li>
        </ul>
      </div>
    </div>
    <!-- By users meter (only if more than one) -->
    <div
      class="divMarginTop"
      v-if="!tempDisableUsersBreakdown && (topUsers?.length || 0) > 1"
    >
      <h4 class="smallText nomargintopbottom">By users</h4>
      <div class="smallText grayText" v-if="topUsersLoading">Processing…</div>
      <div v-else>
        <ul class="smallText">
          <li
            v-for="u in decoratedTopUsers"
            :key="u.userId"
            class="row gap alignCenterRow"
          >
            <img
              v-if="u.profile?.smallPhotoURL"
              :src="u.profile.smallPhotoURL"
              alt=""
              class="usage-user-thumbnail"
              loading="lazy"
            />
            <span
              v-else
              class="avatarFallback usage-user-thumbnail"
              aria-hidden="true"
            ></span>
            <span class="mono tiny">{{ shortId(u.userId) }}</span>
            <span>— writes: {{ u.totals.writes }}</span>
            <span>({{ formatEUR(u.approxCostEUR ?? u.approxCostUSD) }})</span>
          </li>
        </ul>
      </div>
    </div>
    <!-- Daily usage table (collapsed: hide zero days, newest first) -->
    <div class="divMarginTop">
      <h4 class="smallText nomargintopbottom">Usage (daily)</h4>
      <p class="smallText grayText" style="margin-top: var(--space-1)">
        Usage = number of writes (creates/updates). Storage cost is estimated
        per Firestore storage pricing (approx. per KB‑month prorated daily).
      </p>
      <div
        class="tableWrap"
        v-if="!tempDisableDailyTable && usageSeries && usageSeries.length"
      >
        <table class="usageTable smallText">
          <thead>
            <tr>
              <th>Day</th>
              <th>Usage</th>
              <th>Usage Cost</th>
              <th>Δ Storage (KB)</th>
              <th>Storage Cost</th>
              <th>Total Cost</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in usageSeries" :key="d.day">
              <td>{{ prettyDay(d.day) }}</td>
              <td>{{ d.writes }}</td>
              <td>{{ formatEUR(d.usageCostEUR ?? d.usageCostUSD * 0.92) }}</td>
              <td>
                {{ (Number(d.storageBytesDelta || 0) / 1024).toFixed(1) }}
              </td>
              <td>{{ formatEUR(d.storageCostEUR ?? 0) }}</td>
              <td>{{ formatEUR(d.totalCostEUR ?? d.totalCostUSD * 0.92) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else-if="!tempDisableDailyTable" class="smallText grayText">
        No usage yet
      </div>
    </div>

    <div class="row gap" style="margin-top: var(--space-4)">
      <UnitButton
        variant="secondary"
        size="small"
        :disabled="loading"
        @click="refresh"
      >
        {{ loading ? "Refreshing…" : "Refresh" }}
      </UnitButton>
    </div>
  </div>
</template>

<script>
import {
  ref,
  watch,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";
import {
  fetchOrgDailyUsage,
  fetchOrgUsageTotals,
  fetchUsageBreakdown,
  fetchTopUsers,
  fetchOrgUsageSeries,
  fetchUsageHeadline,
} from "@/services/usageService";
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import UsageSummaryTable from "@/components/GenericComponents/UsageSummaryTable.vue";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";

// Register Chart.js components
Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default {
  name: "OrgUsagePanel",
  props: { orgId: { type: String, required: true } },
  components: { UsageSummaryTable, UnitButton },
  setup(props) {
    const usage = ref(null);
    const loading = ref(false);
    const error = ref(null);
    const totals30d = ref(null);
    const totalsAll = ref(null);
    const totalsLoading = ref(false);
    const breakdownLoading = ref(false);
    const topUsersLoading = ref(false);
    const usageSeries = ref([]);
    const breakdown = ref({ bySource: [], byApiKey: [] });
    const topUsers = ref([]);
    const userProfiles = ref({});
    const usageChartCanvas = ref(null);
    let usageChart = null;

    // Derived billing helpers
    const effectiveFreeCapEUR = ref(10); // default 10; replaced from headline or config+extra
    const overageEUR = ref(0);
    const freeRemaining = ref(10);
    const hasPaid = ref(false);
    const headlineMode = ref(null); // 'under' | 'over' | null
    const tempDisableUsersBreakdown = ref(true);
    const tempDisableDailyTable = ref(true);
    const tempDisableChart = ref(true);
    const getCSSVariable = (token) =>
      getComputedStyle(document.documentElement).getPropertyValue(token).trim();

    // Render usage cost chart
    const renderUsageChart = async () => {
      if (tempDisableChart.value) return;
      await nextTick(); // Wait for DOM to update

      if (
        !usageChartCanvas.value ||
        !usageSeries.value ||
        usageSeries.value.length === 0
      ) {
        return;
      }

      // Destroy existing chart
      if (usageChart) {
        usageChart.destroy();
        usageChart = null;
      }

      const ctx = usageChartCanvas.value.getContext("2d");

      // Prepare data (reverse to show oldest to newest left-to-right)
      const sortedData = [...usageSeries.value].reverse();
      const labels = sortedData.map((d) => {
        const day = String(d.day);
        return `${day.slice(4, 6)}-${day.slice(6, 8)}`; // MM-DD format
      });

      const usageCosts = sortedData.map((d) => {
        const cost =
          d.usageCostEUR ?? (d.usageCostUSD ? d.usageCostUSD * 0.92 : 0);
        return Number(cost) * 1000; // Convert to milliEUR for better scale
      });

      const storageCosts = sortedData.map((d) => {
        const cost =
          d.storageCostEUR ?? (d.storageCostUSD ? d.storageCostUSD * 0.92 : 0);
        return Number(cost) * 1000; // Convert to milliEUR
      });

      const totalCosts = sortedData.map(
        (d, i) => usageCosts[i] + storageCosts[i]
      );

      usageChart = new Chart(ctx, {
        type: "line",
        data: {
          labels,
          datasets: [
            {
              label: "Total Cost (m€)",
              data: totalCosts,
              borderColor: getCSSVariable("--color-data-usage-total"),
              backgroundColor: getCSSVariable(
                "--color-data-usage-total-surface"
              ),
              borderWidth: 2,
              fill: true,
              tension: 0.1,
            },
            {
              label: "Usage Cost (m€)",
              data: usageCosts,
              borderColor: getCSSVariable("--color-data-usage-api"),
              backgroundColor: getCSSVariable(
                "--color-data-usage-api-surface"
              ),
              borderWidth: 1,
              borderDash: [5, 5],
              fill: false,
              tension: 0.1,
            },
            {
              label: "Storage Cost (m€)",
              data: storageCosts,
              borderColor: getCSSVariable("--color-data-usage-storage"),
              backgroundColor: getCSSVariable(
                "--color-data-usage-storage-surface"
              ),
              borderWidth: 1,
              borderDash: [2, 2],
              fill: false,
              tension: 0.1,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: "index",
            intersect: false,
          },
          plugins: {
            legend: {
              display: true,
              position: "top",
              labels: {
                font: { size: 10 },
                boxWidth: 15,
                padding: 8,
              },
            },
            tooltip: {
              callbacks: {
                label: function (context) {
                  let label = context.dataset.label || "";
                  if (label) {
                    label += ": €";
                  }
                  // Convert back from milli-EUR to EUR
                  const value = (context.parsed.y / 1000).toFixed(8);
                  label += value;
                  return label;
                },
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                font: { size: 9 },
                callback: function (value) {
                  return "€" + (value / 1000).toFixed(3);
                },
              },
              title: {
                display: true,
                text: "Cost (milli-EUR)",
                font: { size: 10 },
              },
            },
            x: {
              ticks: {
                font: { size: 9 },
                maxRotation: 45,
                minRotation: 45,
              },
            },
          },
        },
      });
    };

    const load = async (opts = {}) => {
      const showSpinner = !!opts.showSpinner;
      if (!props.orgId) return;
      loading.value = true;
      error.value = null;
      console.log("OrgUsagePanel.load:request", { orgId: props.orgId });
      if (showSpinner) totalsLoading.value = true;
      // 1) Try to hydrate a super-fast headline from custom-orgs (single, cheap read)
      try {
        const head = await fetchUsageHeadline(props.orgId);
        if (head && head.headline) {
          headlineMode.value = head.headline.mode || null;
          overageEUR.value = Number(head.headline.overageEUR || 0);
          freeRemaining.value = Number(head.headline.freeRemainingEUR ?? 10);
          if (typeof head.headline.effectiveFreeCapEUR === "number") {
            effectiveFreeCapEUR.value = Number(
              head.headline.effectiveFreeCapEUR
            );
          }
        }
        if (head && head.totals30d && !totals30d.value)
          totals30d.value = head.totals30d;
        if (head && head.totalsAll && !totalsAll.value)
          totalsAll.value = head.totalsAll;
      } catch (e) {
        // not fatal; continue
      }
      // The legacy browser fallbacks (config/billing, custom-orgs and
      // orgUsageCached Firestore reads) were retired; the usage service
      // headline is the only source for the effective free cap.
      // 3) Fire all network requests in parallel to minimize TTI
      const dailyP = (async () => {
        try {
          const res = await fetchOrgDailyUsage(props.orgId);
          console.log("OrgUsagePanel.load:response", res);
          usage.value = res;
        } catch (e) {
          console.warn("OrgUsagePanel.load:daily error (continuing)", e);
          if (!error.value) error.value = e;
          usage.value = null;
        }
      })();

      const totalsP = (async () => {
        try {
          console.log("OrgUsagePanel:fetch totals (30d & all-time)", {
            orgId: props.orgId,
          });
          const [t30, tall] = await Promise.all([
            fetchOrgUsageTotals(props.orgId, 30),
            fetchOrgUsageTotals(props.orgId, null),
          ]);
          totals30d.value = t30;
          totalsAll.value = tall;
          console.log("OrgUsagePanel:totals30d response", t30);
          console.log("OrgUsagePanel:totalsAll response", tall);

          // Compute billing as soon as all-time totals are in
          const totalCost = Number(
            (tall?.totalCostEUR ??
              tall?.approxCostEUR ??
              tall?.approxCostUSD) ||
              0
          );
          const usedBeyondFree = Math.max(
            0,
            totalCost - effectiveFreeCapEUR.value
          );
          overageEUR.value = usedBeyondFree;
          freeRemaining.value = Math.max(
            0,
            effectiveFreeCapEUR.value - totalCost
          );
          headlineMode.value = usedBeyondFree === 0 ? "under" : "over";
          hasPaid.value = usedBeyondFree === 0;
        } catch (inner) {
          console.warn("OrgUsagePanel.totals:error", inner);
          if (!error.value) error.value = inner;
        } finally {
          if (showSpinner) totalsLoading.value = false;
        }
      })();

      const seriesP = (async () => {
        try {
          console.log("OrgUsagePanel:fetch usage series (30d)", {
            orgId: props.orgId,
          });
          const series = await fetchOrgUsageSeries(props.orgId, 30);
          const raw = (series && series.days) || [];
          usageSeries.value = raw
            .filter((d) => {
              const writes = Number(d.writes || 0);
              const storage = Number(d.storageBytesDelta || 0);
              const total = Number(
                d.totalCostEUR ??
                  (d.totalCostUSD != null ? d.totalCostUSD * 0.92 : 0)
              );
              return writes > 0 || storage !== 0 || total > 0;
            })
            .sort((a, b) => Number(b.day) - Number(a.day));

          // Render chart after data loaded
          await nextTick();
          renderUsageChart();
        } catch (e) {
          console.warn("OrgUsagePanel.series:error", e);
        }
      })();

      const analyticsP = (async () => {
        try {
          breakdownLoading.value = true;
          topUsersLoading.value = true;
          console.log("OrgUsagePanel:fetch breakdown & top users (30d)");
          let bd = null;
          let tu = null;
          if (tempDisableUsersBreakdown.value) {
            bd = await fetchUsageBreakdown(props.orgId, 30);
            topUsers.value = [];
          } else {
            const res = await Promise.all([
              fetchUsageBreakdown(props.orgId, 30),
              fetchTopUsers(props.orgId, 30, 10),
            ]);
            bd = res[0];
            tu = res[1];
            topUsers.value = (tu && tu.users) || [];
          }
          breakdown.value = bd || { bySource: [], byApiKey: [] };
        } catch (e) {
          console.warn("OrgUsagePanel.analytics:error", e);
        } finally {
          breakdownLoading.value = false;
          topUsersLoading.value = false;
        }
      })();

      // Wait for all background tasks to settle, but don't block UI if any fail
      await Promise.allSettled([dailyP, totalsP, seriesP, analyticsP]);
      loading.value = false;
    };
    const refresh = async () => {
      // Show explicit processing state only on manual refresh
      await load({ showSpinner: true });
    };
    watch(
      () => props.orgId,
      async () => {
        try {
          await load();
        } catch (e) {
          error.value = e;
        }
      },
      { immediate: true }
    );
    // Avoid duplicate load calls: watch with immediate handles initial fetch
    // onMounted(async () => {});
    const formatEUR = (val) => {
      const n = Number(val);
      if (!isFinite(n)) return "—";
      if (n === 0) return "€0.00";
      // Show more precision for micro-amounts
      if (Math.abs(n) < 0.01) return `€${n.toFixed(8)}`;
      return `€${n.toFixed(2)}`;
    };
    const formatSmallCurrency = (val) => {
      const n = Number(val);
      if (!isFinite(n)) return "0.00";
      return n < 1 ? n.toFixed(6) : n.toFixed(2);
    };
    const formatFreeRemaining = (val) => {
      const n = Number(val);
      if (!isFinite(n)) return "0.00";
      const consumed = Math.max(0, effectiveFreeCapEUR.value - n);
      // While consumption is under €1, show micro precision so €10 doesn't mask small deltas
      if (consumed < 1) return n.toFixed(6);
      return n.toFixed(2);
    };
    const decoratedTopUsers = computed(() =>
      (topUsers.value || []).map((u) => ({
        ...u,
        profile: userProfiles.value[u.userId] || null,
      }))
    );
    const shortId = (id) => (id ? `${id.slice(0, 4)}…${id.slice(-4)}` : "");
    const todayStats = computed(() => ({
      writes: usage.value?.totals?.writes || 0,
      storageBytesDelta: usage.value?.totals?.storageBytesDelta || 0,
      approxCostEUR:
        usage.value?.approxCostEUR ??
        (typeof usage.value?.approxCostUSD === "number"
          ? Number((usage.value.approxCostUSD * 0.92).toFixed(6))
          : undefined),
    }));
    const hasStorage = (t) =>
      typeof (t?.storageCostEUR ?? t?.storageCostUSD) === "number" &&
      Number(t.storageCostEUR ?? t.storageCostUSD) > 0;

    const hasMeaningfulBreakdown = (breakdown) => {
      if (!breakdown || typeof breakdown !== "object") return false;
      // Check if any domain has a cost > 0
      const domains = [
        "transactions",
        "messages",
        "nfts",
        "products",
        "services",
        "items",
        "members",
        "externalAPIs",
        "storage",
        "other",
      ];
      return domains.some((domain) => {
        const domainData = breakdown[domain];
        if (!domainData) return false;
        const cost = domainData.costEUR ?? domainData.costUSD ?? 0;
        return Number(cost) > 0;
      });
    };

    // Lifecycle hooks
    onMounted(() => {
      // Charts will be rendered when data loads
    });

    onBeforeUnmount(() => {
      if (usageChart) {
        usageChart.destroy();
        usageChart = null;
      }
    });

    // Watch for changes to usageSeries and re-render chart
    watch(
      usageSeries,
      () => {
        renderUsageChart();
      },
      { deep: true }
    );

    return {
      usage,
      loading,
      refresh,
      error,
      totals30d,
      totalsAll,
      totalsLoading,
      headlineMode,
      breakdownLoading,
      topUsersLoading,
      overageEUR,
      freeRemaining,
      effectiveFreeCapEUR,
      hasPaid,
      usageSeries,
      breakdown,
      topUsers,
      todayStats,
      formatEUR,
      formatSmallCurrency,
      formatFreeRemaining,
      hasStorage,
      hasMeaningfulBreakdown,
      prettyDay: (yyyymmdd) =>
        `${yyyymmdd.slice(0, 4)}-${yyyymmdd.slice(4, 6)}-${yyyymmdd.slice(
          6,
          8
        )}`,
      decoratedTopUsers,
      shortId,
      usageChartCanvas,
      tempDisableUsersBreakdown,
      tempDisableDailyTable,
      tempDisableChart,
    };
  },
};
</script>

<style scoped>
.org-usage-panel {
  background: var(--color-surface-raised);
  padding: var(--space-4);
  border-radius: var(--radius-sm);
}
.priorityBlock {
  margin: var(--space-4) 0;
  text-align: center;
}
.bigNumber {
  font-size: 32px;
  font-weight: 600;
}
.usage-danger-metric {
  margin-top: var(--space-2);
}
.usage-user-thumbnail {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
}
.avatarFallback.usage-user-thumbnail {
  display: inline-block;
  background: var(--color-surface-muted);
}
.twoColList {
  columns: 2;
  column-gap: 2rem;
  list-style: none;
  padding: 0;
}
</style>
